/* Thin fetch wrapper.
 *
 * Goals:
 *   - One place to inject the bearer token and base URL.
 *   - Auto-clear session and redirect on 401 so stale tokens don't loop.
 *   - Surface non-2xx as typed ApiError, never a silent {} from .json().
 *   - Keep the surface minimal — full SWR/React-Query is overkill here.
 */

import { clearSession, loadSession } from './auth';

const RAW_BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? '';
// Empty base means "use Vite's /api proxy" in dev. We still need a real URL
// in production builds — the user must set VITE_API_URL.
export const API_BASE = RAW_BASE.replace(/\/+$/, '');

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.status = status;
    this.body = body;
    this.name = 'ApiError';
  }
}

type Method = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

interface RequestOpts {
  /** Skip auth header even if a session exists (for /auth/* endpoints). */
  anonymous?: boolean;
  /** Abort signal for in-flight cancellation. */
  signal?: AbortSignal;
  /** Extra headers (override defaults). */
  headers?: Record<string, string>;
}

async function request<T>(
  method: Method,
  path: string,
  body?: unknown,
  opts: RequestOpts = {},
): Promise<T> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(opts.headers ?? {}),
  };
  if (body !== undefined && !(body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }
  if (!opts.anonymous) {
    const session = loadSession();
    if (session) headers.Authorization = `Bearer ${session.token}`;
  }

  // Backend mounts /auth/* and /health directly (no /api prefix), so those
  // paths must NOT be wrapped in API_BASE — see nginx /(auth|health) location.
  const normalized = path.startsWith('/') ? path : `/${path}`;
  const isBackendRootPath =
    normalized.startsWith('/auth/') ||
    normalized === '/auth' ||
    normalized === '/health';
  const url = isBackendRootPath
    ? normalized
    : API_BASE
      ? `${API_BASE}${normalized}`
      : `/api${normalized}`;

  const res = await fetch(url, {
    method,
    headers,
    body:
      body === undefined
        ? undefined
        : body instanceof FormData
          ? body
          : JSON.stringify(body),
    signal: opts.signal,
  });

  // 401 → drop the dead session and bounce to login. We don't redirect from
  // inside fetch when the caller is the login page itself.
  if (res.status === 401 && !opts.anonymous) {
    clearSession();
    if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
      window.location.assign('/login');
    }
  }

  // 204 / empty body — return undefined as T.
  if (res.status === 204) return undefined as T;

  const text = await res.text();
  let parsed: unknown = undefined;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }

  if (!res.ok) {
    const msg =
      parsed && typeof parsed === 'object' && 'detail' in parsed
        ? String((parsed as { detail: unknown }).detail)
        : `${res.status} ${res.statusText}`;
    throw new ApiError(res.status, msg, parsed);
  }

  return parsed as T;
}

export const apiClient = {
  get: <T>(path: string, opts?: RequestOpts) => request<T>('GET', path, undefined, opts),
  post: <T>(path: string, body?: unknown, opts?: RequestOpts) =>
    request<T>('POST', path, body, opts),
  patch: <T>(path: string, body?: unknown, opts?: RequestOpts) =>
    request<T>('PATCH', path, body, opts),
  put: <T>(path: string, body?: unknown, opts?: RequestOpts) =>
    request<T>('PUT', path, body, opts),
  delete: <T>(path: string, opts?: RequestOpts) => request<T>('DELETE', path, undefined, opts),
};

/** Resolve the WebSocket URL for a given path, picking up the auth token. */
export function resolveWsUrl(path: string): string {
  const explicit = import.meta.env.VITE_WS_URL as string | undefined;
  let base: string;
  if (explicit) {
    base = explicit.replace(/\/+$/, '');
  } else if (API_BASE) {
    base = API_BASE.replace(/^http/i, (m) => (m.toLowerCase() === 'https' ? 'wss' : 'ws'));
  } else {
    // Dev: same origin; Vite proxies /ws.
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    base = `${proto}//${window.location.host}/ws`;
    return `${base}${path.startsWith('/') ? path : `/${path}`}`;
  }
  const session = loadSession();
  const sep = path.includes('?') ? '&' : '?';
  const tokenParam = session ? `${sep}token=${encodeURIComponent(session.token)}` : '';
  return `${base}${path.startsWith('/') ? path : `/${path}`}${tokenParam}`;
}
